import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import moment from "moment";
import {
  Box, Button, ButtonBase, CircularProgress, IconButton, InputBase, Skeleton, Stack, Switch, Table, TableBody,
  TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip, Typography
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {
  Add01Icon, Delete02Icon, Download04Icon, PencilEdit01Icon, PlayIcon, Refresh01Icon, StopIcon, TextFontIcon, VoiceIcon
} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {useRegistryProject} from "../RegistryProjectContext";
import {VocabularyRepository} from "../../../repositories/VocabularyRepository";
import useConfirm from "../../../hooks/useConfirm";
import {useDebounce} from "../../../hooks/useDebounce";
import {PhonemizeCreateModal} from "../../project/vocabulary/components/PhonemizeCreateModal";
import {PhonemizeEditModal} from "../../project/vocabulary/components/PhonemizeEditModal";
import {Panel, PanelHeading, PillSelect} from "../dashboard/components/Panel";
import {SearchHeadFilter} from "../dashboard/components/HeaderFilters";
import {EMPTY_VOICE_OVERRIDES, toAudioOptions, VoiceSettings} from "./VoiceSettings";
import {subProjectLabel} from "../../../utils/subProjects";

// The API answers errors as JSON {error}; with responseType blob it arrives as a Blob
const previewError = async (e) => {
  try {
    const body = JSON.parse(await e?.response?.data?.text?.());
    if (body?.error) return body.error;
  } catch (ignored) {
    // not JSON
  }
  if (e?.response?.status === 502) return "The TTS service failed to synthesize the preview";
  return "Failed to synthesize the preview";
};

const AUDIO_EXTENSIONS = {"audio/wav": "wav", "audio/x-wav": "wav", "audio/wave": "wav", "audio/mpeg": "mp3",
  "audio/mp3": "mp3", "audio/ogg": "ogg", "audio/opus": "opus"};

// File name from the spoken text (what the browser allows in a name), the extension from the audio's type
const audioFileName = (text, type) => {
  const name = text.trim().replace(/[\\/:*?"<>|\s]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 60) || "preview";
  return `${name}.${AUDIO_EXTENSIONS[String(type).split(";")[0].toLowerCase()] || "wav"}`;
};

const TYPES = [
  {value: "PRONUNCIATION", label: "Pronunciation", icon: TextFontIcon,
    description: "Replacement text applied before every synthesis, e.g. ДДВ → де де ве"},
  {value: "PHONEMIZER", label: "Phonemizer", icon: VoiceIcon,
    description: "Phoneme (IPA) spellings for the TTS phonemizer, applied after a reload"},
];

const headCellSx = {
  height: 40, py: 0.5, px: 1.5, fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#8A8AA0",
  bgcolor: "#FAFAFD", borderBottom: "1px solid #ECECF3", whiteSpace: "nowrap",
};
const cellSx = {py: 1, px: 1.5, fontSize: 13, color: "#23264D", borderBottom: "1px solid #F2F2F7"};
const iconButtonSx = {borderRadius: "50% !important", color: "#8A8AA0", "&:hover": {bgcolor: "#F4F4FA", color: "#4A4A63"}};
const fieldSx = {
  display: "flex", alignItems: "center", gap: 0.5, pl: 1.5, pr: 0.5, minHeight: 44, borderRadius: "12px",
  border: "1px solid #E4E4EE", bgcolor: "#fff", "&:focus-within": {borderColor: "#8491F7"},
};
const darkButtonSx = {
  height: 40, px: 2, borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
  color: "#fff", bgcolor: "#23264D", "&:hover": {bgcolor: "#33376B"}, "&.Mui-disabled": {color: "#fff", bgcolor: "#C9CAD8"},
};
const lightButtonSx = {
  height: 40, px: 2, borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
  color: "#23264D", border: "1px solid #E4E4EE", bgcolor: "#fff", "&:hover": {bgcolor: "#F4F4FA"},
};

// Plays TTS previews one at a time, or saves one as a file. playing: key of the preview that is loading or playing;
// downloadingKey: key of the preview being saved.
// options: voice settings to try instead of the flow's current ones (see VoiceSettings).
const usePreview = (subProjectId, options) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(null);
  const [loadingKey, setLoadingKey] = useState(null);
  const [downloadingKey, setDownloadingKey] = useState(null);

  const stop = useCallback(() => {
    audioRef.current?.pause();
    audioRef.current = null;
    setPlaying(null);
  }, []);

  const play = useCallback(async (key, text) => {
    if (!text?.trim() || !subProjectId) return;
    if (playing === key) return stop();
    stop();
    setLoadingKey(key);
    try {
      const response = await VocabularyRepository.getAudio(text, subProjectId, options);
      const url = URL.createObjectURL(response.data);
      const audio = new Audio(url);
      audio.onended = () => {
        URL.revokeObjectURL(url);
        if (audioRef.current === audio) stop();
      };
      audioRef.current = audio;
      setPlaying(key);
      await audio.play();
    } catch (e) {
      console.error(e);
      toast.error(await previewError(e));
    } finally {
      setLoadingKey(null);
    }
  }, [subProjectId, options, playing, stop]);

  // The same audio as play (same voice settings), saved through a temporary link
  const download = useCallback(async (key, text) => {
    if (!text?.trim() || !subProjectId) return;
    setDownloadingKey(key);
    try {
      const response = await VocabularyRepository.getAudio(text, subProjectId, options);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = audioFileName(text, response.data?.type);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error(e);
      toast.error(await previewError(e));
    } finally {
      setDownloadingKey(null);
    }
  }, [subProjectId, options]);

  useEffect(() => stop, [stop, subProjectId]);

  return {play, stop, download, playing, loadingKey, downloadingKey};
};

const PlayButton = ({previewKey, text, preview, title = "Listen"}) => {
  const isLoading = preview.loadingKey === previewKey;
  const isPlaying = preview.playing === previewKey;
  return (
    <Tooltip title={isPlaying ? "Stop" : title}>
      <span>
        <IconButton size={"small"} disabled={!text?.trim() || isLoading} onClick={() => preview.play(previewKey, text)}
                    sx={{...iconButtonSx, ...(isPlaying ? {color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.12)"} : {})}}>
          {isLoading
            ? <CircularProgress size={15} sx={{color: "#8491F7"}}/>
            : <HugeiconsIcon icon={isPlaying ? StopIcon : PlayIcon} size={16} strokeWidth={2}/>}
        </IconButton>
      </span>
    </Tooltip>
  );
};

const DownloadButton = ({previewKey, text, preview}) => {
  const isLoading = preview.downloadingKey === previewKey;
  return (
    <Tooltip title={"Download audio"}>
      <span>
        <IconButton size={"small"} disabled={!text?.trim() || isLoading} onClick={() => preview.download(previewKey, text)}
                    sx={iconButtonSx}>
          {isLoading
            ? <CircularProgress size={15} sx={{color: "#8491F7"}}/>
            : <HugeiconsIcon icon={Download04Icon} size={16} strokeWidth={2}/>}
        </IconButton>
      </span>
    </Tooltip>
  );
};

// Pronunciation | Phonemizer
const TypeToggle = ({value, onChange, counts}) => (
  <Stack direction={"row"} sx={{p: 0.5, gap: 0.5, borderRadius: "12px", border: "1px solid #E4E4EE", bgcolor: "#fff"}}>
    {TYPES.map(type => (
      <ButtonBase key={type.value} onClick={() => onChange(type.value)} sx={{
        height: 30, px: 1.5, gap: 0.75, borderRadius: "9px", fontSize: 13, fontWeight: 500,
        color: value === type.value ? "#4B55C9" : "#6B6B80",
        bgcolor: value === type.value ? "rgba(132, 145, 247, 0.12)" : "transparent",
        "&:hover": {bgcolor: value === type.value ? "rgba(132, 145, 247, 0.18)" : "#F4F4FA"},
      }}>
        <HugeiconsIcon icon={type.icon} size={15}/>
        {type.label}
        {counts?.[type.value] !== undefined && (
          <Box component={"span"} sx={{fontSize: 11, color: "#8A8AA0"}}>{counts[type.value]}</Box>
        )}
      </ButtonBase>
    ))}
  </Stack>
);

// Text-to-speech vocabulary of one sub project. Every call takes the sub project id (the backend finds its flow);
// a sub project without a flow has no vocabulary yet.
export const RegistryVocabularyPage = () => {
  const {project, subProject} = useRegistryProject();
  const [subProjectId, setSubProjectId] = useState(String(subProject.id));
  const language = project.subProjects.find(item => String(item.id) === subProjectId) || subProject;
  const flowUid = language?.flowUid;

  const [type, setType] = useState("PRONUNCIATION");
  const [entries, setEntries] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [phraseSearch, setPhraseSearch] = useState("");
  const [pronunciationSearch, setPronunciationSearch] = useState("");
  const phraseDebounced = useDebounce(phraseSearch, 250);
  const pronunciationDebounced = useDebounce(pronunciationSearch, 250);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // pronunciation form (create / edit)
  const [form, setForm] = useState({phrase: "", pronunciation: "", regex: false});
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sentence, setSentence] = useState("");
  // phonemizer modals
  const [phonemizerCreateOpen, setPhonemizerCreateOpen] = useState(false);
  const [phonemizerEditItem, setPhonemizerEditItem] = useState(null);
  const [reloading, setReloading] = useState(false);

  const [voiceOverrides, setVoiceOverrides] = useState(EMPTY_VOICE_OVERRIDES);
  const audioOptions = useMemo(() => toAudioOptions(voiceOverrides), [voiceOverrides]);
  const preview = usePreview(flowUid ? language.id : null, audioOptions);
  const confirmDelete = useConfirm("Delete entry?", "The phrase will no longer be replaced.", "Delete");
  const typeConfig = TYPES.find(item => item.value === type);

  const load = useCallback(async () => {
    if (!flowUid) {
      setEntries([]);
      setCounts({});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [pronunciation, phonemizer] = await Promise.all(TYPES.map(item =>
        VocabularyRepository.getSubProjectVocabulary(language.id, item.value).then(response => response?.data || [])));
      setCounts({PRONUNCIATION: pronunciation.length, PHONEMIZER: phonemizer.length});
      setEntries(type === "PRONUNCIATION" ? pronunciation : phonemizer);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load vocabulary");
    } finally {
      setLoading(false);
    }
  }, [flowUid, language.id, type]);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setForm({phrase: "", pronunciation: "", regex: false});
    setEditing(null);
  };

  useEffect(() => {
    resetForm();
    setPage(0);
  }, [flowUid, type]);

  useEffect(() => {
    setPage(0);
  }, [phraseDebounced, pronunciationDebounced]);

  const save = async () => {
    if (!form.phrase.trim() || !form.pronunciation.trim()) return;
    setSaving(true);
    const body = {phrase: form.phrase.trim(), pronunciation: form.pronunciation.trim(), regex: form.regex, type};
    try {
      if (editing) {
        // no subProjectId: the entry stays with its sub project
        const response = await VocabularyRepository.update(editing.id, body);
        // the API overwrites another entry that already has this phrase instead of the edited one
        if (response?.data?.id && response.data.id !== editing.id) {
          toast.info(`"${body.phrase}" already existed - that entry was updated instead`);
        } else {
          toast.success("Entry updated");
        }
      } else {
        await VocabularyRepository.create({...body, subProjectId: language.id, projectId: project.id});
        toast.success("Entry added");
      }
      resetForm();
      load();
    } catch (e) {
      console.error(e);
      if (e?.response?.status === 409) toast.error(`"${body.phrase}" already exists`);
      else if (e?.response?.status === 404) toast.error("This entry no longer exists");
      else toast.error(editing ? "Failed to update entry" : "Failed to add entry");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (entry) => {
    if (type === "PHONEMIZER") {
      setPhonemizerEditItem(entry);
      return;
    }
    setEditing(entry);
    setForm({phrase: entry.phrase || "", pronunciation: entry.pronunciation || "", regex: Boolean(entry.regex)});
    window.scrollTo?.({top: 0, behavior: "smooth"});
  };

  const remove = async (entry) => {
    if (!(await confirmDelete({title: `Delete "${entry.phrase}"?`}))) return;
    try {
      await VocabularyRepository.delete(entry.id);
      toast.success("Entry deleted");
      if (editing?.id === entry.id) resetForm();
      load();
    } catch (e) {
      console.error(e);
      toast.error("Failed to delete entry");
    }
  };

  const reloadPhonemizer = async () => {
    setReloading(true);
    try {
      await VocabularyRepository.reloadPhonemizer(language.id);
      toast.success("Phonemizer reloaded");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.status === 504 ? "The TTS service did not answer in time" : "Failed to reload phonemizer");
    } finally {
      setReloading(false);
    }
  };

  const filtered = useMemo(() => entries
    .filter(entry => (entry.phrase || "").toLowerCase().includes(phraseDebounced.toLowerCase()))
    .filter(entry => (entry.pronunciation || "").toLowerCase().includes(pronunciationDebounced.toLowerCase())),
  [entries, phraseDebounced, pronunciationDebounced]);
  const pageEntries = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const languageOptions = project.subProjects.map(item => ({
    value: String(item.id), label: subProjectLabel(item),
  }));
  const flowProject = useMemo(() => (flowUid ? {id: flowUid} : null), [flowUid]);

  return (
    <Stack gap={3} sx={{p: {xs: 2, md: 3}, maxWidth: {xs: "calc(100% - 16px)", md: "calc(100% - 128px)"}, width: "100%", mx: "auto", boxSizing: "border-box"}}>
      {/* Heading */}
      <Stack direction={{xs: "column", md: "row"}} justifyContent={"space-between"} alignItems={{md: "flex-end"}} gap={2}>
        <Box>
          <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7"}}>TEXT-TO-SPEECH</Typography>
          <Typography component={"h1"} sx={{fontSize: 26, fontWeight: 700, color: "#23264D", letterSpacing: "-0.02em"}}>
            Vocabulary
          </Typography>
          <Typography sx={{fontSize: 14, color: "#6B6B80"}}>
            Teach the voice how to say abbreviations, names and foreign words
          </Typography>
        </Box>
        <Stack direction={"row"} alignItems={"center"} gap={1.25} flexWrap={"wrap"}>
          {project.subProjects.length > 1 && (
            <PillSelect value={subProjectId} options={languageOptions} onChange={setSubProjectId}
                        ariaLabel={"Sub project"}/>
          )}
          <TypeToggle value={type} onChange={setType} counts={flowUid ? counts : undefined}/>
        </Stack>
      </Stack>

      {!flowUid ? (
        <Panel sx={{p: 5, alignItems: "center", textAlign: "center"}}>
          <Typography sx={{fontSize: 16, fontWeight: 700, color: "#23264D"}}>
            {language?.languageName || "This language"} has no flow yet
          </Typography>
          <Typography sx={{mt: 0.5, fontSize: 13, color: "#8A8AA0"}}>Vocabulary is stored per flow, so it can be added once the language has one.</Typography>
        </Panel>
      ) : (
        <>
          {/* Add / edit + preview */}
          <Panel sx={{p: 2.5}}>
            <PanelHeading
              kicker={editing ? "EDIT ENTRY" : type === "PHONEMIZER" ? "PHONEMIZER" : "NEW ENTRY"}
              title={editing ? `Editing "${editing.phrase}"` : typeConfig.label}
              description={typeConfig.description}
              actions={type === "PHONEMIZER" ? <>
                <Button sx={lightButtonSx} disabled={reloading} onClick={reloadPhonemizer}
                        startIcon={reloading ? <CircularProgress size={15} sx={{color: "#8491F7"}}/> : <HugeiconsIcon icon={Refresh01Icon} size={16}/>}>
                  Reload phonemizer
                </Button>
                <Button sx={darkButtonSx} onClick={() => setPhonemizerCreateOpen(true)}
                        startIcon={<HugeiconsIcon icon={Add01Icon} size={16}/>}>
                  Add entry
                </Button>
              </> : null}
            />

            {type === "PRONUNCIATION" && (
              <Box sx={{mt: 2, display: "grid", gap: 1.5, gridTemplateColumns: {xs: "1fr", lg: "minmax(0, 1fr) minmax(0, 1fr) auto"}, alignItems: "start"}}>
                <Box>
                  <Typography sx={{mb: 0.5, fontSize: 12, fontWeight: 600, color: "#6B6B80"}}>Phrase</Typography>
                  <Box sx={fieldSx}>
                    <InputBase fullWidth multiline maxRows={3} placeholder={form.regex ? "Regular expression, e.g. \\bДДВ\\b" : "e.g. ДДВ"}
                               value={form.phrase} onChange={(e) => setForm(current => ({...current, phrase: e.target.value}))}
                               sx={{fontSize: 14, fontFamily: form.regex ? "monospace" : undefined}}/>
                    <PlayButton previewKey={"form-phrase"} text={form.phrase} preview={preview} title={"Listen as it is read now"}/>
                    <DownloadButton previewKey={"form-phrase"} text={form.phrase} preview={preview}/>
                  </Box>
                  <Stack direction={"row"} alignItems={"center"} gap={0.5} sx={{mt: 0.5}}>
                    <Switch size={"small"} checked={form.regex}
                            onChange={(e) => setForm(current => ({...current, regex: e.target.checked}))}/>
                    <Typography sx={{fontSize: 12, color: "#6B6B80"}}>Treat the phrase as a regular expression</Typography>
                  </Stack>
                </Box>
                <Box>
                  <Typography sx={{mb: 0.5, fontSize: 12, fontWeight: 600, color: "#6B6B80"}}>Pronunciation</Typography>
                  <Box sx={fieldSx}>
                    <InputBase fullWidth multiline maxRows={3} placeholder={"e.g. де де ве"}
                               value={form.pronunciation}
                               onChange={(e) => setForm(current => ({...current, pronunciation: e.target.value}))}
                               onKeyDown={(e) => e.key === "Enter" && (e.metaKey || e.ctrlKey) && save()}
                               sx={{fontSize: 14}}/>
                    <PlayButton previewKey={"form-pronunciation"} text={form.pronunciation} preview={preview}/>
                    <DownloadButton previewKey={"form-pronunciation"} text={form.pronunciation} preview={preview}/>
                  </Box>
                </Box>
                <Stack direction={"row"} gap={1} sx={{pt: {lg: 2.75}}}>
                  {(editing || form.phrase || form.pronunciation) && (
                    <Button sx={lightButtonSx} onClick={resetForm}>{editing ? "Cancel" : "Clear"}</Button>
                  )}
                  <Button sx={darkButtonSx} disabled={saving || !form.phrase.trim() || !form.pronunciation.trim()} onClick={save}
                          startIcon={saving ? <CircularProgress size={15} sx={{color: "#fff"}}/> : !editing ? <HugeiconsIcon icon={Add01Icon} size={16}/> : null}>
                    {editing ? "Save" : "Add entry"}
                  </Button>
                </Stack>
              </Box>
            )}

            {/* Whole sentence with every pronunciation entry applied, as in a call */}
            <Box sx={{mt: 2.5, pt: 2, borderTop: "1px solid #F0F0F6"}}>
              <Typography sx={{mb: 0.5, fontSize: 12, fontWeight: 600, color: "#6B6B80"}}>Try a sentence</Typography>
              <Box sx={{...fieldSx, bgcolor: "#FAFAFD"}}>
                <InputBase fullWidth placeholder={"Type a sentence and listen to it with this language's vocabulary"}
                           value={sentence} onChange={(e) => setSentence(e.target.value)}
                           onKeyDown={(e) => e.key === "Enter" && preview.play("sentence", sentence)}
                           sx={{fontSize: 14}}/>
                <PlayButton previewKey={"sentence"} text={sentence} preview={preview}/>
                <DownloadButton previewKey={"sentence"} text={sentence} preview={preview}/>
              </Box>
              <VoiceSettings subProjectId={language.id} overrides={voiceOverrides} onChange={setVoiceOverrides}/>
            </Box>
          </Panel>

          {/* Entries */}
          <Panel sx={{overflow: "hidden"}}>
            <Box sx={{p: 2.5, pb: 2}}>
              <PanelHeading
                kicker={"ENTRIES"}
                title={`${typeConfig.label} vocabulary`}
                count={filtered.length}
                description={`${language?.languageName || language?.languageCode || ""} · newest first`}
              />
            </Box>
            <TableContainer sx={{overflowX: "auto"}}>
              <Table size={"small"} sx={{minWidth: 720}}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{...headCellSx, pl: 2.5, width: "38%"}}>
                      <SearchHeadFilter label={"PHRASE"} placeholder={"Search phrase"} value={phraseSearch} onChange={setPhraseSearch}/>
                    </TableCell>
                    <TableCell sx={{...headCellSx, width: "38%"}}>
                      <SearchHeadFilter label={type === "PHONEMIZER" ? "PHONEMES" : "PRONUNCIATION"} placeholder={"Search"}
                                        value={pronunciationSearch} onChange={setPronunciationSearch}/>
                    </TableCell>
                    <TableCell sx={headCellSx}>UPDATED</TableCell>
                    <TableCell sx={{...headCellSx, pr: 2.5}}/>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && entries.length === 0 && Array.from({length: 4}).map((_, index) => (
                    <TableRow key={index}><TableCell colSpan={4} sx={{...cellSx, px: 2.5}}><Skeleton height={28}/></TableCell></TableRow>
                  ))}
                  {pageEntries.map(entry => (
                    <TableRow key={entry.id} hover sx={{
                      bgcolor: editing?.id === entry.id ? "rgba(132, 145, 247, 0.08)" : "transparent",
                      "&.MuiTableRow-hover:hover": {bgcolor: "#FAFAFD"}, "&:last-of-type td": {borderBottom: 0},
                    }}>
                      <TableCell sx={{...cellSx, pl: 2.5, fontWeight: 600}}>
                        <Stack direction={"row"} alignItems={"center"} gap={1}>
                          <Box component={"span"} sx={{fontFamily: entry.regex ? "monospace" : undefined, wordBreak: "break-word"}}>{entry.phrase}</Box>
                          {entry.regex && (
                            <Tooltip title={"Matched as a regular expression"}>
                              <Box component={"span"} sx={{
                                px: 0.75, py: 0.125, borderRadius: "6px", fontSize: 10, fontWeight: 600, letterSpacing: "0.04em",
                                color: "#6E5BC4", bgcolor: "#F1EEFB", flexShrink: 0,
                              }}>REGEX</Box>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{...cellSx, color: "#4A4A63", wordBreak: "break-word", fontFamily: type === "PHONEMIZER" ? "monospace" : undefined}}>
                        {entry.pronunciation}
                      </TableCell>
                      <TableCell sx={{...cellSx, whiteSpace: "nowrap", color: "#8A8AA0", fontSize: 12}}>
                        {entry.dateUpdated || entry.dateCreated ? moment(entry.dateUpdated || entry.dateCreated).format("DD MMM YYYY") : "—"}
                      </TableCell>
                      <TableCell sx={{...cellSx, pr: 2.5, whiteSpace: "nowrap"}}>
                        <Stack direction={"row"} gap={0.25} justifyContent={"flex-end"}>
                          {type === "PRONUNCIATION" && (
                            <>
                              <PlayButton previewKey={`entry-${entry.id}`} text={entry.pronunciation} preview={preview}/>
                              <DownloadButton previewKey={`entry-${entry.id}`} text={entry.pronunciation} preview={preview}/>
                            </>
                          )}
                          <Tooltip title={"Edit"}>
                            <IconButton size={"small"} sx={iconButtonSx} onClick={() => startEdit(entry)}>
                              <HugeiconsIcon icon={PencilEdit01Icon} size={16}/>
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={"Delete"}>
                            <IconButton size={"small"} sx={{...iconButtonSx, "&:hover": {bgcolor: "#FDECEC", color: "#C73A3A"}}}
                                        onClick={() => remove(entry)}>
                              <HugeiconsIcon icon={Delete02Icon} size={16}/>
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} sx={{...cellSx, borderBottom: 0, py: 6, textAlign: "center", color: "#8A8AA0"}}>
                        {entries.length ? "No entries match your search" : "No entries yet"}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component={"div"}
              count={filtered.length}
              page={Math.min(page, Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1))}
              onPageChange={(e, next) => setPage(next)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              sx={{borderTop: "1px solid #ECECF3", color: "#6B6B80"}}
            />
          </Panel>
        </>
      )}

      <PhonemizeCreateModal
        open={phonemizerCreateOpen}
        onClose={() => setPhonemizerCreateOpen(false)}
        onSuccess={load}
        projectId={flowUid}
        owner={{subProjectId: language.id, projectId: project.id}}
      />
      <PhonemizeEditModal
        open={Boolean(phonemizerEditItem)}
        onClose={() => setPhonemizerEditItem(null)}
        onSuccess={load}
        project={flowProject}
        item={phonemizerEditItem}
        vocabularyType={"PHONEMIZER"}
        keepOwner
      />
    </Stack>
  );
};
